/**
 * EmailJS Configuration
 * 
 * To set up EmailJS:
 * 1. Go to https://www.emailjs.com/ and create a free account
 * 2. Create an email service (Gmail, Outlook, etc.)
 * 3. Create an email template with the following variables:
 *    - {{from_name}} - Sender's name
 *    - {{from_email}} - Sender's email
 *    - {{subject}} - Email subject
 *    - {{message}} - Email message body
 * 4. Get your Public Key, Service ID, and Template ID from the EmailJS dashboard
 * 5. Add them to your .env file (see .env.example)
 * 
 * Environment Variables:
 * REACT_APP_EMAILJS_PUBLIC_KEY - Your EmailJS Public Key
 * REACT_APP_EMAILJS_SERVICE_ID - Your EmailJS Service ID
 * REACT_APP_EMAILJS_TEMPLATE_ID - Your EmailJS Template ID
 * REACT_APP_RECAPTCHA_SITE_KEY - Your Google reCAPTCHA v3 Site Key (optional but recommended)
 */

export const emailjsConfig = {
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '',
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
  recaptchaSiteKey: process.env.REACT_APP_RECAPTCHA_SITE_KEY || '',
};

// Rate limiting configuration
export const rateLimitConfig = {
  maxSubmissions: 3, // Maximum submissions per time window
  timeWindow: 60 * 60 * 1000, // 1 hour in milliseconds
  storageKey: 'contact_form_submissions',
};

// Validate configuration
export const isEmailJSConfigured = () => {
  return !!(
    emailjsConfig.publicKey &&
    emailjsConfig.serviceId &&
    emailjsConfig.templateId
  );
};

// Check if reCAPTCHA is configured
export const isRecaptchaConfigured = () => {
  return !!emailjsConfig.recaptchaSiteKey;
};

// Rate limiting utility functions
export const checkRateLimit = () => {
  try {
    const stored = localStorage.getItem(rateLimitConfig.storageKey);
    if (!stored) {
      return { allowed: true, remaining: rateLimitConfig.maxSubmissions };
    }

    const submissions = JSON.parse(stored);
    const now = Date.now();
    
    // Filter out submissions outside the time window
    const recentSubmissions = submissions.filter(
      (timestamp) => now - timestamp < rateLimitConfig.timeWindow
    );

    if (recentSubmissions.length >= rateLimitConfig.maxSubmissions) {
      const oldestSubmission = recentSubmissions[0];
      const timeUntilReset = rateLimitConfig.timeWindow - (now - oldestSubmission);
      const minutesUntilReset = Math.ceil(timeUntilReset / (60 * 1000));
      
      return {
        allowed: false,
        remaining: 0,
        minutesUntilReset,
      };
    }

    // Update localStorage with filtered submissions
    localStorage.setItem(rateLimitConfig.storageKey, JSON.stringify(recentSubmissions));

    return {
      allowed: true,
      remaining: rateLimitConfig.maxSubmissions - recentSubmissions.length,
    };
  } catch (error) {
    console.error('Rate limit check error:', error);
    // On error, allow submission (fail open)
    return { allowed: true, remaining: rateLimitConfig.maxSubmissions };
  }
};

export const recordSubmission = () => {
  try {
    const stored = localStorage.getItem(rateLimitConfig.storageKey);
    const submissions = stored ? JSON.parse(stored) : [];
    const now = Date.now();
    
    // Add current submission timestamp
    submissions.push(now);
    
    // Keep only submissions within the time window
    const recentSubmissions = submissions.filter(
      (timestamp) => now - timestamp < rateLimitConfig.timeWindow
    );
    
    localStorage.setItem(rateLimitConfig.storageKey, JSON.stringify(recentSubmissions));
  } catch (error) {
    console.error('Rate limit record error:', error);
  }
};

